import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileStack,
  Award,
  Users,
  CreditCard,
  Clock,
  ArrowRight,
  ScrollText,
  CheckCircle2,
  Building2,
  Calendar,
  Gavel,
  RefreshCw,
  GraduationCap,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, hint, accent }) => (
  <Card className="p-6 border-slate-200 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{label}</div>
        <div className="font-heading text-3xl font-bold text-primary mt-2">{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent || "bg-primary/5 text-primary"}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentApps, setRecentApps] = useState([]);

  useEffect(() => {
    const url = ["admin", "reviewer"].includes(user?.role) ? "/admin/stats-full" : "/dashboard/stats";
    api.get(url).then((r) => setStats(r.data)).catch(() => setStats({}));
    if (user?.role === "applicant") {
      api.get("/schools/applications").then((r) => setRecentApps(r.data.slice(0, 5))).catch(() => {});
    } else if (user?.role !== "site_visitor") {
      api.get("/applications").then((r) => setRecentApps(r.data.slice(0, 5))).catch(() => {});
    }
  }, [user?.role]);

  const role = user?.role;

  return (
    <div data-testid="dashboard-home" className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Overview</div>
          <h1 className="font-heading text-3xl font-bold text-primary">Welcome, {user?.name?.split(" ")[0] || "there"}.</h1>
          <p className="text-sm text-slate-500 mt-1">Your credentialing workspace · <span className="capitalize">{(role || "").replace(/_/g, " ")}</span> role.</p>
        </div>
        {role === "applicant" && (
          <Link to="/dashboard/schools/new">
            <Button data-testid="quick-new-app-btn" className="bg-primary text-white hover:bg-[#113A64]">
              Start an Accreditation Application <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        )}
        {role === "practitioner" && (
          <Link to="/dashboard/applications/new">
            <Button data-testid="quick-new-app-btn" className="bg-primary text-white hover:bg-[#113A64]">
              Start a new application <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        )}
      </div>

      {(role === "admin" || role === "reviewer") ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard icon={FileStack} label="IBTHM applications" value={stats?.applications ?? "—"} hint={`${stats?.submitted ?? 0} new`} />
            <StatCard icon={Award} label="Active credentials" value={stats?.active_credentials ?? "—"} accent="bg-emerald-50 text-emerald-700" />
            <StatCard icon={CreditCard} label="Pending payments" value={stats?.pending_payments ?? "—"} accent="bg-blue-50 text-blue-700" />
            <StatCard icon={RefreshCw} label="Pending renewals" value={stats?.pending_renewals ?? "—"} accent="bg-violet-50 text-violet-700" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Building2} label="Accredited schools" value={stats?.schools_accredited ?? "—"} hint={`${stats?.schools_total ?? 0} total`} accent="bg-indigo-50 text-indigo-700" />
            <StatCard icon={Calendar} label="Open school apps" value={stats?.school_apps_open ?? "—"} accent="bg-amber-50 text-amber-700" />
            <StatCard icon={Calendar} label="Site visits scheduled" value={stats?.site_visits_scheduled ?? "—"} accent="bg-orange-50 text-orange-700" />
            <StatCard icon={Gavel} label="Open disciplinary" value={stats?.open_disciplinary ?? "—"} accent="bg-red-50 text-red-700" />
          </div>
        </>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={FileStack} label="My applications" value={stats?.my_applications ?? "—"} />
          <StatCard icon={Clock} label="In progress" value={stats?.pending ?? "—"} accent="bg-amber-50 text-amber-700" />
          <StatCard icon={Award} label="Active credentials" value={stats?.my_credentials ?? "—"} accent="bg-emerald-50 text-emerald-700" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {role !== "site_visitor" ? (
          <Card className="lg:col-span-2 p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-primary">
                {role === "applicant" ? "Recent accreditation applications" : "Recent applications"}
              </h2>
              <Link
                to={role === "applicant" ? "/dashboard/schools" : "/dashboard/applications"}
                className="text-xs text-primary hover:underline"
              >
                View all →
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recentApps.length === 0 && (
                <div className="text-sm text-slate-500 py-6 text-center">No applications yet.</div>
              )}
              {recentApps.map((a) => {
                const isSchool = role === "applicant";
                const detailHref = isSchool
                  ? `/dashboard/schools/${a.id}`
                  : `/dashboard/applications/${a.id}`;
                const titleText = isSchool ? a.name : a.applicant_name;
                return (
                  <Link key={a.id} to={detailHref} className="flex items-center justify-between py-4 group" data-testid={`recent-app-${a.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        {isSchool ? (
                          <>
                            <Badge className="bg-primary text-white text-[10px]">ACIS</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700 capitalize text-[10px]">
                              {a.accreditation_type || a.institution_type || "institutional"}
                            </Badge>
                          </>
                        ) : (
                          <>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700 font-mono-data text-[10px]">{a.credential_type}</Badge>
                            <Badge className="bg-primary text-white text-[10px]">{a.organization}</Badge>
                          </>
                        )}
                      </div>
                      <div className="text-sm font-medium text-primary truncate">{titleText}</div>
                      <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <AppStatusBadge status={a.status} />
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-primary transition" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </Card>
        ) : (
          <Card className="lg:col-span-2 p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-primary">Site visit assignments</h2>
              <Link to="/dashboard/site-visits" className="text-xs text-primary hover:underline">View all →</Link>
            </div>
            <p className="text-sm text-slate-600">Open the site visits queue to review schools assigned to you and submit your visit report.</p>
            <Link to="/dashboard/site-visits" className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              Go to my site visits <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Card>
        )}

        <Card className="p-6 border-slate-200">
          <h2 className="font-heading font-semibold text-primary mb-4">Quick actions</h2>
          <div className="space-y-2">
            {(role === "applicant") && (
              <>
                <QuickLink to="/dashboard/schools/new" icon={Building2} label="Apply for institutional accreditation" />
                <QuickLink to="/dashboard/schools" icon={FileStack} label="My accreditation applications" />
                <QuickLink to="/dashboard/documents" icon={ScrollText} label="Upload supporting documents" />
                <QuickLink to="/dashboard/accredited-schools" icon={Award} label="Accreditation status" />
                <QuickLink to="/dashboard/applications/new" icon={FileStack} label="Apply for IBTHM Board Certification" />
              </>
            )}
            {(role === "practitioner") && (
              <>
                <QuickLink to="/dashboard/applications/new" icon={FileStack} label="Start new application" />
                <QuickLink to="/dashboard/schools/new" icon={Building2} label="Apply for school accreditation" />
                <QuickLink to="/dashboard/documents" icon={ScrollText} label="Upload documents" />
                <QuickLink to="/dashboard/ce" icon={GraduationCap} label="Log CE hours" />
                <QuickLink to="/dashboard/credentials" icon={Award} label="View my credentials" />
              </>
            )}
            {(role === "reviewer") && (
              <>
                <QuickLink to="/dashboard/applications" icon={FileStack} label="Open IBTHM review queue" />
                <QuickLink to="/dashboard/schools" icon={Building2} label="ACIS accreditation queue" />
                <QuickLink to="/dashboard/site-visits" icon={Calendar} label="Site visits" />
                <QuickLink to="/dashboard/credentials" icon={Award} label="Browse credentials" />
              </>
            )}
            {(role === "site_visitor") && (
              <>
                <QuickLink to="/dashboard/site-visits" icon={Calendar} label="My site visits" />
                <QuickLink to="/dashboard/schools" icon={Building2} label="School applications" />
              </>
            )}
            {(role === "admin") && (
              <>
                <QuickLink to="/dashboard/schools" icon={Building2} label="ACIS · School applications" />
                <QuickLink to="/dashboard/site-visits" icon={Calendar} label="ACIS · Site visits" />
                <QuickLink to="/dashboard/applications" icon={FileStack} label="IBTHM · Applications" />
                <QuickLink to="/dashboard/payments" icon={CreditCard} label="Verify payments" />
                <QuickLink to="/dashboard/disciplinary" icon={Gavel} label="Disciplinary cases" />
                <QuickLink to="/dashboard/users" icon={Users} label="Manage users" />
                <QuickLink to="/dashboard/audit" icon={ScrollText} label="Audit logs" />
              </>
            )}
          </div>
          <div className="border-t border-slate-200 mt-6 pt-5 text-xs text-slate-500 leading-relaxed flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600" />
            <span>Every action you take here is recorded in the audit log for compliance.</span>
          </div>
        </Card>
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors text-sm">
      <span className="flex items-center gap-2.5 text-slate-700">
        <Icon className="h-4 w-4 text-primary" />
        {label}
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
    </Link>
  );
}

function AppStatusBadge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    submitted: "bg-blue-50 text-blue-800 border-blue-200",
    under_review: "bg-amber-50 text-amber-800 border-amber-200",
    info_requested: "bg-orange-50 text-orange-800 border-orange-200",
    site_visit_scheduled: "bg-violet-50 text-violet-800 border-violet-200",
    site_visit_complete: "bg-indigo-50 text-indigo-800 border-indigo-200",
    decision_pending: "bg-orange-50 text-orange-800 border-orange-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    denied: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <Badge variant="outline" className={`capitalize ${map[status] || map.draft}`}>{(status || "").replace(/_/g, " ")}</Badge>
  );
}
